import { pageTitle } from '@palaccount/config';
import type { ProjectTaskResponse, TaskPriority } from '@palaccount/contracts';
import { Lock, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr, Pct } from '@/components/amount';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableCell } from '@/components/ui/table';
import { PersonSourceHint } from '@/features/hr/person-source';
import { Modal } from '@/features/inventory/modal';
import { Typeahead } from '@/components/ui/typeahead';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useAccounts,
  useAddDependency,
  useBoard,
  useCreateProjectTask,
  useRemoveDependency,
  useSetTaskStatus,
  useTickTask,
  useUpdateProjectTask,
} from './api';
import { useFirstProject, useProjectPicker } from './project-picker';
import { ProjectsTabs } from './projects-tabs';

/**
 * PRJ-02 — Tasks: the ONE task engine's project face. Board and List over
 * the project's own configurable statuses (BR-PRJ-02-05); header counts
 * cover EVERY task including subtasks while the views draw top-level only
 * (BR-PRJ-02-11). Actual hours are the server's approved-entry rollup —
 * "Rolled up from timesheet entries — not manually edited" — and BLOCKED
 * is derived, drawn as the red edge + padlock whatever the status says
 * (BR-PRJ-02-06/07). Moving a blocked task to Done is allowed and surfaces
 * the exact warning (BR-PRJ-02-08). Tasks, estimates and dependencies are
 * all editable (BR-PRJ-02-14); the subtask tick is always present and
 * un-ticking RESTORES the previous status (BR-PRJ-02-15 + defect fix).
 */

const PRIORITY_DOT: Record<TaskPriority, string> = {
  high: 'bg-red-500',
  normal: 'bg-blue-500',
  low: 'bg-muted-foreground',
};

/**
 * The words behind those three colours, for the list table's Priority column
 * (ERR-PRJ-02-07). They are the CRM's own priority labels rather than new
 * ones: `NewTaskModal`'s priority select already offers exactly these three
 * strings, so the column and the field a person sets it with cannot drift
 * apart into two vocabularies for one value.
 *
 * A `Record<TaskPriority, …>` and not a lookup with a fallback, on purpose: a
 * fourth priority added to the contract fails the typecheck here instead of
 * quietly rendering as "Normal".
 */
const PRIORITY_LABEL: Record<TaskPriority, string> = {
  high: 'crm.prHigh',
  normal: 'crm.prNormal',
  low: 'crm.prLow',
};

const STATUS_TONE: Record<string, string> = {
  gray: 'bg-muted text-muted-foreground',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

export function TasksPage() {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState<string>();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>();
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('prj.tasksTitle'));
  }, [t]);

  // GEN-50 + NFR-42 — see billing-page: a searched selector, and an opening
  // project that is one row rather than the head of a whole-catalogue read.
  const { data: firstProject } = useFirstProject();
  const effectiveProjectId = projectId ?? firstProject?.id;
  const projectPicker = useProjectPicker(effectiveProjectId ?? null);
  const { data: board } = useBoard(effectiveProjectId);

  const tasks = useMemo(() => board?.tasks ?? [], [board]);
  const topLevel = tasks.filter((task) => task.parentTaskId === null);
  const selected = tasks.find((task) => task.taskId === selectedTaskId);

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized (`PROJECT_TASK_MESSAGES` are mapped in STATIC_MESSAGE_KEYS),
      // a request that never answered gets this screen's own sentence.
      setError(localizeServerCause(t, cause, 'prj.errSaveFailedTask'));
    });
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('prj.eyebrow')}
        </p>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold">{t('prj.tasksTitle')}</h1>
          {/*
            ERR-PRJ-02-01 — "Tried to add a task: **No control exists** → See
            the defects list." THIS BUTTON IS THAT DEFECT CLOSED, and the row
            is named here so nobody reads it as a statement about this build.

            All three of PRJ-02's "no control exists" rows come from one entry
            in its "Prototype defects to fix at build time" table: "Creating
            and editing | **No way to create a task, edit an estimate, or add
            a dependency** | BUILD ALL THREE." The §14 rows are the prototype's
            symptoms and the defects table is the instruction; the build
            followed the instruction, which is why §14 now reads as false.

            The other two are marked at their own controls:
              ERR-PRJ-02-02  the dependency picker, in TaskDetail
              ERR-PRJ-02-03  the estimate field, in TaskDetail

            The FOURTH is closed now too, and this paragraph used to say it was
            not: ERR-PRJ-02-07, "Sorting by priority is written but has no
            column". Priority reached the screen only as the coloured dot on a
            board card (`PRIORITY_DOT`), and the list table's five columns were
            Title, Assignee, Due, Status and Hours. It is cited at the column
            itself, in the list table below.
          */}
          {/* No project chosen, nothing to add a task to. */}
          {effectiveProjectId !== undefined ? (
            <Gated permission="projects.create" reasonKey="perm.cannot.createTasks">
              <Button onClick={() => setFormOpen(true)}>
                <Plus className="me-1 h-4 w-4" />
                {t('prj.newTask')}
              </Button>
            </Gated>
          ) : null}
        </div>
      </div>
      <ProjectsTabs />

      {error !== '' ? (
        <p role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      ) : null}
      {/* BR-PRJ-02-08: the move went THROUGH; this only reports it. */}
      {warning !== '' ? (
        <p role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          {warning}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Typeahead
          label={t('prj.projectSelector')}
          value={effectiveProjectId ?? null}
          onChange={(id) => {
            setProjectId(id ?? undefined);
            setSelectedTaskId(undefined);
          }}
          options={projectPicker.options}
          selected={projectPicker.optionFor(effectiveProjectId)}
          query={projectPicker.query}
          onQueryChange={projectPicker.setQuery}
          emptyLabel={t('picker.noMatchingProject')}
          loading={projectPicker.loading}
          className="w-64"
        />
        <div className="flex gap-1">
          <Button
            variant={view === 'board' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('board')}
          >
            {t('prj.viewBoard')}
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('list')}
          >
            {t('prj.viewList')}
          </Button>
        </div>
      </div>

      {board ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 p-4 text-sm">
            <span className="font-semibold">
              {t('prj.headerDone', { done: board.header.doneCount, total: board.header.totalCount })}
            </span>
            <Pct value={board.header.completionPct} className="tabular-nums" />
            <span className="text-muted-foreground">
              {/* NFR-01: one island for the run (the `/` between two separate
                  islands would reorder in RTL), unit text from the dictionary. */}
              <Ltr
                value={`${t('unit.hours', { value: board.header.actualHours })} / ${t('unit.hours', { value: board.header.estimatedHours })}`}
              />{' '}
              {t('prj.estVsActual')}
            </span>
            <span className="flex flex-wrap gap-2">
              {board.header.perStatus.map((status) => (
                <span
                  key={status.statusId}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                    STATUS_TONE[status.color] ?? STATUS_TONE.gray,
                  )}
                >
                  {status.name} {status.count}
                </span>
              ))}
            </span>
          </CardContent>
        </Card>
      ) : null}

      {board && view === 'board' ? (
        <div className="grid gap-3 overflow-x-auto md:grid-cols-5">
          {board.statuses.map((status) => (
            <div key={status.id} className="min-w-56 space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {status.name} · {topLevel.filter((task) => task.statusId === status.id).length}
              </p>
              {topLevel.filter((task) => task.statusId === status.id).length === 0 ? (
                <p className="text-center text-muted-foreground">—</p>
              ) : (
                topLevel
                  .filter((task) => task.statusId === status.id)
                  .map((task) => (
                    <TaskCard
                      key={task.taskId}
                      task={task}
                      selected={task.taskId === selectedTaskId}
                      onSelect={() => setSelectedTaskId(task.taskId)}
                    />
                  ))
              )}
            </div>
          ))}
        </div>
      ) : null}

      {board && view === 'list' ? (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-3 text-start">{t('prj.colTask')}</th>
                  {/*
                    ERR-PRJ-02-07 — "Looked for a priority column to sort:
                    **Sorting by priority is written but has no column**."
                    PRJ-02's own defects table gives the whole instruction in
                    three words: "Add the column."

                    IT SITS BESIDE THE TITLE ON PURPOSE. On a board card the
                    priority dot is drawn immediately before the title
                    (`PRIORITY_DOT` in `TaskCard`), so a reader who switches
                    from Board to List finds the same two things next to each
                    other rather than having to re-learn the row.

                    AND IT CARRIES A WORD, NOT ONLY THE DOT. The board can
                    afford a bare colour because a card is scanned, but a
                    column is read, and a column whose entire content is a
                    coloured circle says nothing to anyone reading it aloud or
                    unable to separate red from blue. The dot keeps the visual
                    tie to the board; the label is what makes the column a
                    column.

                    NOT BUILT, deliberately: the header does not sort. §14's
                    row says a priority SORT exists and lacks a column — that
                    is the prototype. This build's list has no sort on any of
                    its headers (`projectTasks` orders by `t.created_at`), so
                    making this one header sortable would leave five that do
                    not and imply an ordering the server never applies. The
                    defects table asked for the column; the column is what
                    this is.
                  */}
                  <th className="p-3 text-start">{t('prj.priority')}</th>
                  <th className="p-3 text-start">{t('prj.colAssignee')}</th>
                  <th className="p-3 text-start">{t('prj.colDue')}</th>
                  <th className="p-3 text-start">{t('prj.colStatus')}</th>
                  <th className="p-3 text-end">{t('prj.colEstActual')}</th>
                </tr>
              </thead>
              <tbody>
                {topLevel.length === 0 ? (
                  <tr>
                    <TableCell colSpan={6} className="px-6 text-center text-muted-foreground">
                      {t('prj.emptyTasks')}
                    </TableCell>
                  </tr>
                ) : (
                  topLevel.map((task) => (
                    <tr
                      key={task.taskId}
                      onClick={() => setSelectedTaskId(task.taskId)}
                      className={cn(
                        'cursor-pointer border-b last:border-0 hover:bg-muted/50',
                        task.blocked && 'border-s-2 border-s-red-500 bg-red-50/40 dark:bg-red-950/20',
                      )}
                    >
                      <TableCell className="px-3 font-medium">
                        {task.blocked ? <Lock className="me-1 inline h-3 w-3 text-red-600 dark:text-red-400" /> : null}
                        {task.title}
                      </TableCell>
                      <TableCell className="px-3">
                        <span className="flex items-center gap-2">
                          <span className={cn('h-2 w-2 shrink-0 rounded-full', PRIORITY_DOT[task.priority])} />
                          {t(PRIORITY_LABEL[task.priority])}
                        </span>
                      </TableCell>
                      <TableCell className="px-3">{task.assigneeName ?? '—'}</TableCell>
                      <TableCell className="px-3">
                        <Ltr value={task.dueAt.slice(0, 10)} />
                      </TableCell>
                      <TableCell className="px-3">{task.statusName}</TableCell>
                      <TableCell className="px-3 text-end tabular-nums">
                        <Ltr value={`${task.actualHours} / ${task.estimatedHours ?? '—'}`} />
                      </TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      {selected && board ? (
        <TaskDetail
          key={selected.taskId}
          task={selected}
          allTasks={tasks}
          statuses={board.statuses}
          onWarning={setWarning}
          act={act}
          onClose={() => setSelectedTaskId(undefined)}
        />
      ) : null}

      {formOpen && effectiveProjectId !== undefined ? (
        <NewTaskModal
          projectId={effectiveProjectId}
          tasks={tasks}
          onClose={() => setFormOpen(false)}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

function TaskCard({
  task,
  selected,
  onSelect,
}: {
  task: ProjectTaskResponse;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full rounded-md border bg-background p-3 text-start text-sm shadow-sm hover:bg-muted/40',
        // BR-PRJ-02-07: the red edge + padlock, whatever the status says.
        task.blocked && 'border-s-4 border-s-red-500',
        selected && 'ring-2 ring-primary',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('h-2 w-2 rounded-full', PRIORITY_DOT[task.priority])} />
        <span className="font-medium">{task.title}</span>
        {task.blocked ? <Lock className="h-3 w-3 text-red-600 dark:text-red-400" /> : null}
      </div>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>{task.assigneeName ?? '—'}</span>
        <Ltr value={task.dueAt.slice(0, 10)} />
      </div>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        {task.subtaskCount > 0 ? (
          <span>
            {task.subtaskDoneCount}/{task.subtaskCount}
          </span>
        ) : (
          <span />
        )}
        <Ltr value={`${task.actualHours}/${task.estimatedHours ?? '—'}`} />
      </div>
    </button>
  );
}

function TaskDetail({
  task,
  allTasks,
  statuses,
  onWarning,
  act,
  onClose,
}: {
  task: ProjectTaskResponse;
  allTasks: ProjectTaskResponse[];
  statuses: { id: string; name: string; isDone: boolean }[];
  onWarning: (message: string) => void;
  act: (promise: Promise<unknown>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canEdit = useCan('projects.edit');
  const setStatus = useSetTaskStatus();
  const tick = useTickTask();
  const update = useUpdateProjectTask();
  const addDependency = useAddDependency();
  const removeDependency = useRemoveDependency();
  const [estimate, setEstimate] = useState(task.estimatedHours ?? '');
  const [newBlocker, setNewBlocker] = useState('');

  const subtasks = allTasks.filter((candidate) => candidate.parentTaskId === task.taskId);
  const blockerCandidates = allTasks.filter(
    (candidate) =>
      candidate.taskId !== task.taskId &&
      !task.blockedBy.some((dep) => dep.taskId === candidate.taskId),
  );

  const move = (statusId: string) => {
    onWarning('');
    act(
      setStatus.mutateAsync({ taskId: task.taskId, statusId }).then((result) => {
        if (result.warning !== null) onWarning(result.warning);
      }),
    );
  };

  return (
    <Modal title={task.title} onClose={onClose} className="max-w-xl">
      <div className="space-y-4">
        {task.blocked ? (
          <p className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
            <Lock className="h-3 w-3" /> {t('prj.blockedPill')}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prj-task-status">{t('prj.colStatus')}</Label>
            <NativeSelect
              id="prj-task-status"
              value={task.statusId}
              onChange={(event) => move(event.target.value)}
            >
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            {/* ERR-PRJ-02-03 — "Tried to edit an estimate: No control
                exists." Closed; see the note on the New task button. */}
            <Label htmlFor="prj-task-estimate">{t('prj.estimatedHours')}</Label>
            <div className="flex gap-2">
              <Input
                id="prj-task-estimate"
                inputMode="decimal"
                value={estimate}
                onChange={(event) => setEstimate(event.target.value)}
                disabled={!canEdit}
              />
              <Gated permission="projects.edit" reasonKey="perm.cannot.editTasks">
                <Button
                  variant="outline"
                  onClick={() =>
                    act(
                      update.mutateAsync({
                        taskId: task.taskId,
                        body: { estimatedHours: estimate === '' ? null : estimate },
                      }),
                    )
                  }
                >
                  {t('common.save')}
                </Button>
              </Gated>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">{t('prj.estimatedHours')}</p>
            <p className="text-lg font-semibold tabular-nums">
              <Ltr value={task.estimatedHours ?? '—'} />
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">{t('prj.actualHours')}</p>
            <p className="text-lg font-semibold tabular-nums">
              <Ltr value={task.actualHours} />
            </p>
            {/* BR-PRJ-02-02: never typed.

                ERR-PRJ-02-06 — "Typed into actual hours: **it is not an
                input** — hours come from timesheets." The enforcement is that
                this is a <p>, not an <input>, and an absence like that is
                exactly what a later "make the panel editable" pass removes
                without noticing. BR-PRJ-02-02 says why it cannot be typed:
                actual hours must be the SUM OF APPROVED TIMESHEET ENTRIES for
                the task, so a typed figure would not be wrong by a little —
                it would be a number with no timesheet behind it, contradicting
                the approvals it is supposed to summarise.

                `prj.rolledUp` below is the screen telling the reader that, so
                the remedy in §14's third column ("Log the hours on a
                timesheet") is discoverable from the panel itself rather than
                only from the spec. */}
            <p className="text-xs text-muted-foreground">{t('prj.rolledUp')}</p>
          </div>
        </div>

        {/* BR-PRJ-02-15: the subtask list and its tick, ALWAYS present. */}
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{t('prj.subtasks')}</h3>
          {subtasks.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noSubtasks')}</p>
          ) : (
            <ul className="space-y-1">
              {subtasks.map((subtask) => (
                <li key={subtask.taskId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={subtask.statusIsDone}
                    aria-label={t('prj.tickSubtask', { name: subtask.title })}
                    onChange={() =>
                      act(
                        tick
                          .mutateAsync({ taskId: subtask.taskId, tick: !subtask.statusIsDone })
                          .then((result) => {
                            if (result.warning !== null) onWarning(result.warning);
                          }),
                      )
                    }
                  />
                  <span className={cn(subtask.statusIsDone && 'text-muted-foreground line-through')}>
                    {subtask.title}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* BR-PRJ-02-14: dependencies are EDITABLE — the cycle check is live. */}
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{t('prj.blockedBy')}</h3>
          {task.blockedBy.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noDependencies')}</p>
          ) : (
            <ul className="space-y-1">
              {task.blockedBy.map((dep) => (
                <li key={dep.taskId} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        dep.done ? 'bg-emerald-500' : 'bg-red-500',
                      )}
                    />
                    {dep.title}
                  </span>
                  {/* GEN-58 DELIBERATELY DOES NOT REACH HERE — same argument as
                      the project-team removal in `projects-page.tsx`. Dropping a
                      dependency between two tasks CAN be undone by adding it
                      back, so GEN-58's mandated "This cannot be undone." would
                      be false if this asked it. A rule whose own sentence cannot
                      be said honestly at a site does not govern that site. */}
                  <Gated permission="projects.edit" reasonKey="perm.cannot.editTasks">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        act(
                          removeDependency.mutateAsync({
                            taskId: task.taskId,
                            blockedByTaskId: dep.taskId,
                          }),
                        )
                      }
                    >
                      {t('common.delete')}
                    </Button>
                  </Gated>
                </li>
              ))}
            </ul>
          )}
          {/* ERR-PRJ-02-02 — "Tried to add a dependency: No control exists,
              ALTHOUGH THE CYCLE CHECK IS WRITTEN AND WAITING." Both halves are
              closed together, and that pairing is the point: the defects table
              asks to "wire it into the editor", so a picker that did not reach
              the guard would close the visible half and leave the dangerous
              one. `useAddDependency` posts to the endpoint that runs it. */}
          <GatedGroup permission="projects.edit" reasonKey="perm.cannot.editTasks">
            <div className="flex items-end gap-2">
              <NativeSelect
                aria-label={t('prj.addDependency')}
                value={newBlocker}
                onChange={(event) => setNewBlocker(event.target.value)}
                className="w-56"
              >
                <option value="">{t('prj.addDependency')}</option>
                {blockerCandidates.map((candidate) => (
                  <option key={candidate.taskId} value={candidate.taskId}>
                    {candidate.title}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                size="sm"
                disabled={newBlocker === ''}
                onClick={() => {
                  act(
                    addDependency.mutateAsync({
                      taskId: task.taskId,
                      blockedByTaskId: newBlocker,
                    }),
                  );
                  setNewBlocker('');
                }}
              >
                {t('common.save')}
              </Button>
            </div>
          </GatedGroup>
        </div>
      </div>
    </Modal>
  );
}

function NewTaskModal({
  projectId,
  tasks,
  onClose,
  onError,
}: {
  projectId: string;
  tasks: ProjectTaskResponse[];
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateProjectTask();
  // The assignee is an ACCOUNT — a project task lands in that person's own
  // My Tasks (BR-PRJ-02-01), which only a signed-in person has.
  const { data: people } = useAccounts();
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  // G-09: a new task's due date starts as the company's today.
  const [due, setDue] = useDocumentDate();
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [estimate, setEstimate] = useState('');
  const [parentTaskId, setParentTaskId] = useState('');

  return (
    <Modal title={t('prj.newTask')} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <Label htmlFor="prj-task-title">{t('prj.fTaskTitle')}</Label>
          <Input
            id="prj-task-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prj-task-assignee">{t('prj.colAssignee')}</Label>
            <NativeSelect
              id="prj-task-assignee"
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
            >
              <option value="">{t('prj.assigneeMe')}</option>
              {(people ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </NativeSelect>
            {/* BR-HR-01-16 — the rule's own named example: "a task assignee
                comes from user accounts". This is the ACCOUNTS list — an
                employee with no login cannot be assigned a task, and the
                hint is what tells the user that is by design. */}
            <PersonSourceHint source="accounts" />
          </div>
          <div>
            <Label htmlFor="prj-task-due">{t('prj.colDue')}</Label>
            <Input
              id="prj-task-due"
              type="date"
              value={due}
              onChange={(event) => setDue(event.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prj-task-priority">{t('prj.priority')}</Label>
            <NativeSelect
              id="prj-task-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
            >
              <option value="high">{t('crm.prHigh')}</option>
              <option value="normal">{t('crm.prNormal')}</option>
              <option value="low">{t('crm.prLow')}</option>
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="prj-task-est">{t('prj.estimatedHours')}</Label>
            <Input
              id="prj-task-est"
              inputMode="decimal"
              value={estimate}
              onChange={(event) => setEstimate(event.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="prj-task-parent">{t('prj.parentTask')}</Label>
          <NativeSelect
            id="prj-task-parent"
            value={parentTaskId}
            onChange={(event) => setParentTaskId(event.target.value)}
          >
            <option value="">—</option>
            {tasks
              .filter((task) => task.parentTaskId === null)
              .map((task) => (
                <option key={task.taskId} value={task.taskId}>
                  {task.title}
                </option>
              ))}
          </NativeSelect>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={title.trim() === '' || due === '' || create.isPending}
            onClick={() => {
              onError('');
              create
                .mutateAsync({
                  projectId,
                  body: {
                    title: title.trim(),
                    dueAt: new Date(`${due}T12:00:00Z`).toISOString(),
                    priority,
                    ...(assigneeId !== '' ? { assigneeId } : {}),
                    ...(estimate !== '' ? { estimatedHours: estimate } : {}),
                    ...(parentTaskId !== '' ? { parentTaskId } : {}),
                  },
                })
                .then(onClose)
                .catch((cause: unknown) => {
                  onError(localizeServerCause(t, cause, 'prj.errSaveFailedTask'));
                });
            }}
          >
            {t('prj.createTask')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
